// (C) Copyright 2020 CNCC Services <https://www.cncc.fr/>
// This program is the sole property of CNCC Services and is not free software.

import {
  ArrowLeftOutlined,
  EditOutlined,
  LoadingOutlined,
  QuestionCircleOutlined,
  SendOutlined
} from "@ant-design/icons";
import {
  DeleteOutlined,
  SaveOutlined,
  StopOutlined
} from "@ant-design/icons/lib";
import { navigate } from "@reach/router";
import { Button, Divider, Form, Modal, Popconfirm, Spin, message } from "antd";
import i18n from "i18next";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  KeywordInfoResponseDTO,
  ThematicDossierInfoResponseDTO,
  ThematicDossierWriteRequestDTO
} from "../../../../api/backend-openapi";
import {
  requestDeleteThematicDossier,
  requestGetThematicDossier,
  requestUnpublishThematicDossier,
  requestUpdateThematicDossier
} from "../../../../state/thematicDossier/ThematicDossierEffects";
import { addThematicDossier } from "../../../../state/thematicDossier/ThematicDossierEvents";
import { formItemBlockLayout } from "../../../../utils/Constant";
import { displayError } from "../../../../utils/displayError";
import { validate } from "../../../../validator/ThematicDossierValidator";
import { showConfirm } from "../../../components/ConfirmModal";
import { Field, OptionType } from "../../../types/NewsTypes";
import { ThematicDossierEditFormProps } from "../../../types/ThematicDossierTypes";
import { ValidatorResult } from "../../../types/Types";
import ThematicDossierInputFormSection from "./ThematicDossierInputFormSection";
import ThematicDossierStatusTag from "./ThematicDossierStatusTag";
import { useStore } from "effector-react";
import { KeywordStore } from "../../../../state/keyword/KeywordStore";
import ThematicDossierColorFormSection from "./ThematicDossierColorFormSection";
import { requestGetKeywords } from "../../../../state/keyword/KeywordEffects";

const { confirm } = Modal;

const ThematicDossierForm: React.FunctionComponent<ThematicDossierEditFormProps> = (
  props: ThematicDossierEditFormProps
) => {
  const {
    thematicDossierId,
    editMode,
    setEditMode,
    canEdit,
    canDelete,
    canValidate
  } = props;
  const { t } = useTranslation();

  const [form] = Form.useForm();

  const [
    thematicDossier,
    setThematicDossier
  ] = useState<ThematicDossierInfoResponseDTO | null>(null);

  const [fields, setFields] = useState<Field[]>([]);

  const [buttonLoading, setButtonLoading] = useState<boolean>(false);

  const [color, setColor] = useState<string>();

  const [getResponseReceived, setGetResponseReceived] = useState<boolean>(
    false
  );

  const keywordsStore: KeywordInfoResponseDTO[] | null = useStore(KeywordStore);

  const confirmationPopup = async (publish?: boolean) => {
    return new Promise<boolean>(resolve => {
      confirm({
        title: t("global.confirm"),
        content: publish
          ? t("thematic_dossier.publish")
          : publish !== undefined
          ? t("thematic_dossiers.table.confirmation.unpublish")
          : t("thematic_dossier.save"),
        okText: t("global.yes"),
        cancelText: t("global.no"),
        centered: true,
        onOk() {
          resolve(true);
        },
        onCancel() {
          resolve(false);
        }
      });
    });
  };

  useEffect(() => {
    if (thematicDossierId) {
      requestGetThematicDossier(thematicDossierId);
    }
  }, [thematicDossierId]);

  const resetFormFields = (data: ThematicDossierInfoResponseDTO) => {
    setColor(data.color);
    setFields([
      {
        name: ["name"],
        value: data.name
      },
      {
        name: ["description"],
        value: data.description || ""
      },
      ...(data.keywordIds
        ? [
            {
              name: ["keywords"],
              value: keywordsStore
                .filter(keyword => (data.keywordIds || []).includes(keyword.id))
                .map(keyword => ({
                  label: keyword.text,
                  value: keyword.id
                }))
            }
          ]
        : [])
    ]);
  };

  useEffect(() => {
    return requestGetKeywords.done.watch(({ result }) => {
      if (result.ok && result.data) {
        if (thematicDossier) {
          resetFormFields(thematicDossier);
        }
      } else {
        displayError("keywords.load_failed");
      }
    });
  });

  useEffect(() => {
    return requestGetThematicDossier.done.watch(({ result }) => {
      setGetResponseReceived(true);
      if (result.ok && result.data) {
        setThematicDossier(result.data);
        resetFormFields(result.data);
      } else {
        displayError("thematic_dossiers.not_found");
      }
    });
  });

  useEffect(() => {
    return requestUpdateThematicDossier.done.watch(({ result }) => {
      setButtonLoading(false);
      if (result.ok && result.data) {
        addThematicDossier(result.data);
        message.success(t("edit_thematic_dossier.success"));
        navigate("/thematicDossiers");
      } else {
        displayError(result.errorMessage || "Error!", false);
      }
    });
  });

  useEffect(() => {
    return requestUnpublishThematicDossier.done.watch(({ result }) => {
      setButtonLoading(false);
      if (result.ok && result.data) {
        addThematicDossier(result.data);
        message.success(t("edit_thematic_dossier.success"));
        navigate("/thematicDossiers");
      } else {
        displayError(result.errorMessage || "Error!", false);
      }
    });
  });

  useEffect(() => {
    return requestDeleteThematicDossier.done.watch(({ result }) => {
      if (result.ok) {
        message.success(t("thematic_dossiers.global_buttons.delete.success"));
        navigate("/thematicDossiers");
      } else {
        displayError("global.on_finish_failed");
      }
    });
  });

  const handleSubmit = (
    values: { [key: string]: string | undefined | string[] },
    publish?: boolean
  ) => {
    const validationResult: ValidatorResult = validate({ ...values, color });
    if (!validationResult.validate) {
      displayError(validationResult.message);
    } else {
      confirmationPopup(publish).then((confirmed: boolean) => {
        if (confirmed) {
          setButtonLoading(true);
          if (publish === false) {
            const thematicDossierToUpdate: { published: boolean } = {
              published: false
            };

            requestUnpublishThematicDossier({
              id: thematicDossierId,
              dto: thematicDossierToUpdate
            });
          } else {
            const thematicDossierToUpdate: ThematicDossierWriteRequestDTO = {
              name: (values.name as string | undefined) || "",
              published: publish || false,
              keywordIds: (values.keywords as unknown[]).map(keyword => {
                if (typeof keyword === "string") {
                  return keyword;
                } else {
                  const keywordOption = keyword as OptionType;
                  if ("value" in keywordOption) {
                    return keywordOption.value;
                  }
                }
                return "";
              }),
              color: color || "",
              description: (values.description as string | undefined) || ""
            };
            requestUpdateThematicDossier({
              id: thematicDossierId,
              dto: thematicDossierToUpdate
            });
          }

          setButtonLoading(true);
        }
      });
    }
  };

  const handleSave = (values: any) => {
    handleSubmit(values);
  };

  const handlePublish = () => {
    form
      .validateFields()
      .then(values => {
        handleSubmit(values, true);
      })
      .catch(() => {
        displayError("global.on_finish_failed");
      });
  };
  const handleUnpublish = () => {
    form
      .validateFields()
      .then(values => {
        handleSubmit(values, false);
      })
      .catch(() => {
        displayError("global.on_finish_failed");
      });
  };

  const spinIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />;

  const onFinishFailed = ({ errorFields }: any) => {
    displayError("global.on_finish_failed");
    form.scrollToField(errorFields[0].name);
  };

  return (
    <>
      {!getResponseReceived ? (
        <div style={{ textAlign: "center" }}>
          <Spin indicator={spinIcon} />
        </div>
      ) : thematicDossier ? (
        <Form
          {...formItemBlockLayout}
          form={form}
          onFinish={handleSave}
          onFinishFailed={onFinishFailed}
          fields={fields}
        >
          <div className="form-first-item-sticky-top-container">
            <div className="form-first-item-sticky-top-content">
              <div className="d-flex align-items-center justify-content-between flex-wrap px-3">
                <div className="my-3">
                  <ThematicDossierStatusTag
                    publicationDate={thematicDossier.publicationDate}
                  />
                </div>
                <Divider orientation="left">
                  <h3 className="text-secondary mb-0">
                    {t("news.news_actions")}
                  </h3>
                </Divider>
                <div className="my-3">
                  {editMode ? (
                    <>
                      <Popconfirm
                        title={t("global.cancel_confirmation")}
                        okText={t("global.yes")}
                        cancelText={t("global.no")}
                        onConfirm={() => {
                          setEditMode(false);
                          resetFormFields(thematicDossier);
                        }}
                        placement="top"
                        icon={<QuestionCircleOutlined />}
                        className="m-2"
                      >
                        <Button htmlType="reset">
                          <StopOutlined /> {t("global.cancel")}
                        </Button>
                      </Popconfirm>
                    </>
                  ) : (
                    <>
                      <div className="d-flex align-items-center justify-content-center flex-wrap">
                        <Button
                          htmlType="reset"
                          className="m-2"
                          onClick={() => navigate("/thematicDossiers")}
                        >
                          <ArrowLeftOutlined />{" "}
                          {t("edit_thematic_dossier.return")}
                        </Button>
                      </div>
                    </>
                  )}
                </div>
                <div className="my-3">
                  {editMode ? (
                    <>
                      {thematicDossier.publicationDate === null && (
                        <Button
                          className="btn-primary m-2"
                          htmlType="submit"
                          loading={buttonLoading}
                        >
                          <SaveOutlined /> {t("global.save")}
                        </Button>
                      )}
                      {canValidate && (
                        <Button
                          className="btn-success m-2"
                          onClick={handlePublish}
                          loading={buttonLoading}
                        >
                          <SendOutlined /> {t("global.publish")}
                        </Button>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="d-flex align-items-center justify-content-center flex-wrap">
                        {canValidate &&
                          thematicDossier.publicationDate === null && (
                            <Button
                              className="btn-success m-2"
                              onClick={handlePublish}
                              loading={buttonLoading}
                            >
                              <SendOutlined /> {t("global.publish")}
                            </Button>
                          )}

                        {canValidate &&
                          thematicDossier.publicationDate !== null && (
                            <Button
                              className="btn-secondary mr-1 btn-list"
                              onClick={handleUnpublish}
                              loading={buttonLoading}
                            >
                              <StopOutlined /> {t("thematic_dossier.unpublish")}
                            </Button>
                          )}
                        {canEdit &&
                          ((canValidate &&
                            thematicDossier.publicationDate !== null) ||
                            thematicDossier.publicationDate === null) && (
                            <Button
                              htmlType="reset"
                              className="btn-primary m-2"
                              onClick={() => setEditMode(true)}
                            >
                              <EditOutlined />{" "}
                              {t("edit_thematic_dossier.button")}
                            </Button>
                          )}
                        {canDelete && thematicDossier.publicationDate === null && (
                          <Button
                            className="btn-danger m-2"
                            onClick={() =>
                              showConfirm(
                                i18n.t(
                                  "thematic_dossiers.table.confirmation.delete",
                                  {
                                    news: thematicDossier.name
                                  }
                                ),
                                () => {
                                  requestDeleteThematicDossier(thematicDossier);
                                }
                              )
                            }
                          >
                            <DeleteOutlined />{" "}
                            {t("edit_thematic_dossier.delete")}
                          </Button>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
          <Divider orientation="left">
            <h3 className="text-secondary mb-0">
              {t("thematic_dossier.infos")}
            </h3>
          </Divider>
          <ThematicDossierInputFormSection
            thematicDossier={thematicDossier}
            editMode={editMode}
          />
          <ThematicDossierColorFormSection
            color={color}
            setColor={setColor}
            editMode={editMode}
          />
        </Form>
      ) : (
        <div>{t("thematic_dossier.not_found")}</div>
      )}
    </>
  );
};
export default ThematicDossierForm;
